import startCase from 'lodash/startCase';

import { AccountType, UnencryptedData } from '@ndvr/portfolio-api';

/**
 * Returns the "Title Case" version of a string.
 * Strips punctuation.
 *
 * @param input Input string
 * @returns Title case version of string
 */
export const titleCase = (input?: string): string => (input ? startCase(input.toLowerCase()) : '');

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
    `${count} ${count === 1 ? singular : plural}`;

/**
 * Wrapper around Number constructor to handle undefined input
 * Converts a number string to number
 * @param numberString string to be converted to number
 *
 * @returns {} number if the number string is valid
 */
export function toNumber(numberString?: string | null): number | undefined {
    if (numberString === null || numberString === '') {
        return;
    }
    const number = Number(numberString);
    if (isNaN(number)) {
        return;
    }
    return number;
}

/**
 * Converts a name into initials
 * @param name object with firstName and lastName
 *
 * @returns string of initials
 */
export function getInitials({ firstName, lastName }: { firstName?: UnencryptedData; lastName?: UnencryptedData }) {
    return `${(firstName ? firstName.plainText[0] : '').toUpperCase()}${(lastName
        ? lastName.plainText[0]
        : ''
    ).toUpperCase()}`;
}

export function getAddress(address?: {
    state?: string;
    street?: UnencryptedData;
    city?: UnencryptedData;
    postalCode?: UnencryptedData;
}): string {
    return [address?.street?.plainText, address?.city?.plainText, address?.state, address?.postalCode?.plainText]
        .filter((x) => !!x)
        .join(' ');
}

export function searchProps(searchString: string, ...props: Array<string | undefined>) {
    const search = searchString.toLowerCase();
    return props.find((prop) => prop && prop.toLowerCase().includes(search));
}

export function formatPhoneNumber(phoneNumberString?: string) {
    if (phoneNumberString) {
        const match = phoneNumberString.replace(/-/g, '').match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
    }
}

export const formatTaxId = (taxId?: string) => taxId?.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');

export const formatEin = (ein?: string) => ein?.replace(/(\d{2})(\d{7})/, '$1-$2');

export const sanitizeText = (text: string) => text.replace(/[.+*?^$()[\]{}|\\]+/g, ''); // replace all Regexp related special characters

export const asNumber = (text = '') => (isNaN(Number(text)) ? text : Number(text).toLocaleString());

export const formatAccountType = (accountType: AccountType): string => {
    switch (accountType) {
        case AccountType.ROTH_IRA:
            return 'Roth IRA';
        case AccountType.SEP_IRA:
            return 'SEP IRA';
        case AccountType.TRADITIONAL_IRA:
            return 'Traditional IRA';
        case AccountType.ROTH_401K:
            return 'Roth 401(k)';
        case AccountType.TRADITIONAL_401K:
            return 'Traditional 401(k)';
        case AccountType.CHARITY:
            return 'Donor Advised Fund';
        case AccountType.PLAN_529:
            return '529 Plan';
        default:
            return titleCase(accountType);
    }
};

export function includesIgnoreCase(text: string | undefined, searchString: string | undefined): boolean {
    if (!text || !searchString) {
        return false;
    }
    return text.toLowerCase().includes(searchString.toLowerCase());
}
